import React, { useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { Link, useNavigate } from "react-router-dom";
import TopMenuBar from "components/TopMenuBar";
import FooterSection from "components/FooterSection";
import { Heading, Text } from "components";

const steps = [
  {
    id: "entry",
    label: "WeChat Entry",
    title: "Trusted entry point",
    description: "The guest gets a reservation-aware WeChat message with one clear action: talk to the AI front desk.",
  },
  {
    id: "voice",
    label: "H5 Voice",
    title: "Voice support session",
    description: "The guest opens an H5 voice session from WeChat and gets live help with arrival, access, and in-stay issues.",
  },
  {
    id: "followup",
    label: "Follow-up",
    title: "Persistent WeChat summary",
    description: "The call becomes a structured message with Wi-Fi, rules, and next steps so the guest does not need to remember spoken instructions.",
  },
  {
    id: "ops",
    label: "Operator",
    title: "Operator action view",
    description: "The operator sees summary, escalation reason, and service-quality markers instead of a dead-end transcript.",
  },
];

const journeyModes = {
  access: {
    label: "Access issue",
    entryPrompt: "Need help finding the entrance or entering the property?",
    voiceGuest: "I’m at the wrong entrance and the code is not working.",
    voiceAi: "You’re close. Please walk to the wooden gate beside the tea shop. I’ll stay with you while you enter the keypad code.",
    followup: [
      "You’re checked in.",
      "Final entry path: wooden gate beside the tea shop.",
      "Keypad confirmed. Human escalation not needed.",
    ],
    opsSummary: "Arrival confusion resolved by AI. Likely signage or map-pin issue worth reviewing at the property level.",
    opsActions: ["Review transcript", "Mark signage issue", "Update check-in instructions"],
  },
  parking: {
    label: "Parking help",
    entryPrompt: "Need help with route, parking, or entry?",
    voiceGuest: "I found the building, but where should I park?",
    voiceAi: "Use Bay B2 behind the bamboo wall. I’ve sent a pin and parking photo in WeChat so you can keep it open while driving.",
    followup: [
      "Parking instructions sent.",
      "Bay: B2 behind bamboo wall.",
      "Photo and pin saved in WeChat for re-entry.",
    ],
    opsSummary: "Parking help resolved without escalation. This path proves voice + persistent message handoff instead of pure call support.",
    opsActions: ["Review parking flow", "Upload better parking photo", "Track repeat parking questions"],
  },
  wifi: {
    label: "In-stay help",
    entryPrompt: "Need help with route, parking, or something during the stay?",
    voiceGuest: "I’m inside now. Can you resend the Wi-Fi and checkout time?",
    voiceAi: "Done. I’ve sent Wi-Fi, checkout time, and quiet hours in WeChat so you don’t need to remember it from the call.",
    followup: [
      "Wi-Fi sent: HiddenPine_3A",
      "Checkout time: 11:00 AM",
      "Quiet hours and support options included.",
    ],
    opsSummary: "In-stay support resolved with no human involvement. This is where the product reduces repetitive operator messages after check-in.",
    opsActions: ["Inspect support transcript", "Add more FAQ cards", "Track repeat in-stay questions"],
  },
  human: {
    label: "Escalation path",
    entryPrompt: "Need urgent help? Talk to AI first and we’ll pull in a human if needed.",
    voiceGuest: "The keypad still fails. I need a person now.",
    voiceAi: "Understood. I’m escalating this to the on-call host now and sending them your exact issue summary so you won’t need to repeat yourself.",
    followup: [
      "Human escalation started.",
      "On-call host notified with transcript.",
      "Guest receives fallback contact and ETA.",
    ],
    opsSummary: "Guest requested a human after failed access flow. Escalation includes summary, urgency, and failed steps already attempted.",
    opsActions: ["Acknowledge escalation", "Call guest now", "Review lock or keypad failure"],
  },
};

const pageShell = {
  maxWidth: 1180,
  margin: "0 auto",
  padding: "0 48px",
};

const panel = {
  border: "1px solid rgba(255,255,255,0.08)",
  borderRadius: 24,
  background: "linear-gradient(160deg, rgba(16,16,22,0.95), rgba(8,8,12,0.98))",
  boxShadow: "0 18px 48px rgba(0,0,0,0.32)",
};

const fieldStyle = {
  width: "100%",
  padding: "0.78rem 0.9rem",
  borderRadius: 14,
  border: "1px solid rgba(255,255,255,0.12)",
  background: "rgba(255,255,255,0.04)",
  color: "#fff",
  fontFamily: "Manrope, sans-serif",
  fontSize: "0.88rem",
  outline: "none",
};

function buildFrontDeskPersonaPrompt(property, mode) {
  const issueInstruction = {
    access: "Prioritize arrival, route correction, gate selection, keypad guidance, and calm troubleshooting.",
    parking: "Prioritize route clarification, parking instructions, and follow-up message delivery.",
    wifi: "Prioritize in-stay support, Wi-Fi answers, checkout reminders, and concise follow-up.",
    human: "Prioritize clear escalation, acknowledge urgency, summarize failed steps, and promise human follow-up.",
  }[mode] || "Prioritize post-booking guest support and escalation when needed.";

  return [
    `You are the AI front desk for ${property.propertyName} in ${property.city}.`,
    "You are speaking with a guest after booking, not a pre-sales lead.",
    `Check-in window: ${property.checkInWindow}.`,
    `Wi-Fi name or note: ${property.wifi}.`,
    `Parking guidance: ${property.parking}.`,
    `Quiet hours or house-rule summary: ${property.quietHours}.`,
    `Human escalation contact summary: ${property.escalationContact}.`,
    "Speak like a calm, human front-desk host. Be warm, direct, and operationally useful.",
    "If you do not know something property-specific, say you will check and hand off instead of inventing it.",
    issueInstruction,
  ].join(" ");
}

async function ensureDemoSession() {
  const response = await fetch("/.netlify/functions/demo-session", {
    method: "POST",
    credentials: "same-origin",
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(payload.error || "Could not create a demo session.");
  }
}

function Badge({ children }) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        fontFamily: "Manrope, sans-serif",
        fontSize: "11px",
        fontWeight: 700,
        letterSpacing: "0.1em",
        textTransform: "uppercase",
        color: "rgba(255,255,255,0.62)",
        border: "1px solid rgba(255,255,255,0.12)",
        borderRadius: 999,
        padding: "6px 14px",
        background: "rgba(255,255,255,0.03)",
      }}
    >
      {children}
    </span>
  );
}

function StepPill({ active, children, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding: "0.72rem 0.95rem",
        borderRadius: 999,
        border: active ? "1px solid rgba(115,215,255,0.92)" : "1px solid rgba(255,255,255,0.12)",
        background: active
          ? "linear-gradient(135deg, rgba(115,215,255,0.16), rgba(217,140,95,0.12))"
          : "rgba(255,255,255,0.03)",
        color: "#fff",
        fontFamily: "Manrope, sans-serif",
        fontSize: "0.82rem",
        fontWeight: 700,
        cursor: "pointer",
      }}
    >
      {children}
    </button>
  );
}

function PhoneShell({ children }) {
  return (
    <div style={{ ...panel, maxWidth: 368, padding: 12, borderRadius: 30, margin: "0 auto" }}>
      <div style={{ minHeight: 660, borderRadius: 22, overflow: "hidden", background: "linear-gradient(180deg, #f5efe7 0%, #ede3d6 100%)", position: "relative" }}>
        <div style={{ width: 92, height: 20, borderRadius: 999, background: "#0b0d10", margin: "12px auto 0" }} />
        {children}
      </div>
    </div>
  );
}

function ModeChip({ active, children, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding: "0.55rem 0.8rem",
        borderRadius: 999,
        border: active ? "1px solid rgba(31,159,98,0.9)" : "1px solid rgba(255,255,255,0.12)",
        background: active ? "rgba(31,159,98,0.14)" : "rgba(255,255,255,0.04)",
        color: active ? "#1f5a3f" : "#2d3a33",
        fontFamily: "Manrope, sans-serif",
        fontSize: "0.75rem",
        fontWeight: 800,
        cursor: "pointer",
      }}
    >
      {children}
    </button>
  );
}

function EntryScreen({ modeKey, onSelectMode, property }) {
  const mode = journeyModes[modeKey];
  return (
    <PhoneShell>
      <div style={{ padding: "18px 16px 20px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 14 }}>
          <div style={{ fontFamily: "Manrope, sans-serif", fontWeight: 800, color: "#18342a" }}>{property.propertyName}</div>
          <div style={{ fontFamily: "Manrope, sans-serif", fontSize: "0.76rem", color: "#60796c" }}>WeChat</div>
        </div>
        <div style={{ display: "grid", gap: 12 }}>
          <div style={{ background: "#ffffff", borderRadius: 18, padding: "12px 14px", boxShadow: "0 10px 24px rgba(0,0,0,0.08)" }}>
            <div style={{ fontFamily: "Manrope, sans-serif", fontSize: "0.86rem", color: "#2d3a33", lineHeight: 1.6 }}>
              Hi {property.guestName}. Check-in starts at <strong>{property.checkInWindow}</strong>.
              <br />
              {mode.entryPrompt}
            </div>
          </div>
          <div style={{ background: "#daf4e2", borderRadius: 18, padding: "12px 14px", boxShadow: "0 10px 24px rgba(0,0,0,0.06)" }}>
            <div style={{ fontFamily: "Manrope, sans-serif", fontSize: "0.78rem", color: "#214534", lineHeight: 1.55 }}>
              Choose the guest’s reason for opening support:
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 10 }}>
              {Object.entries(journeyModes).map(([key, value]) => (
                <ModeChip
                  key={key}
                  active={key === modeKey}
                  onClick={() => onSelectMode(key)}
                >
                  {value.label}
                </ModeChip>
              ))}
            </div>
          </div>
          <div style={{ justifySelf: "center", width: 184, height: 184, borderRadius: 24, background: "#fff", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 12px 28px rgba(0,0,0,0.08)" }}>
            <div style={{ width: 118, height: 118, borderRadius: 12, background: "repeating-linear-gradient(90deg, #173126 0 8px, #e7f1ea 8px 16px), repeating-linear-gradient(180deg, #173126 0 8px, #e7f1ea 8px 16px)" }} />
          </div>
        </div>
        <div style={{ position: "absolute", left: 16, right: 16, bottom: 18 }}>
          <div style={{ width: "100%", padding: "0.95rem 1rem", borderRadius: 16, background: "linear-gradient(135deg, #4ac27f, #1f9f62)", color: "#fff", textAlign: "center", fontFamily: "Manrope, sans-serif", fontWeight: 800 }}>
            Talk to AI front desk
          </div>
        </div>
      </div>
    </PhoneShell>
  );
}

function VoiceScreen({ modeKey, property }) {
  const mode = journeyModes[modeKey];
  return (
    <PhoneShell>
      <div style={{ padding: "22px 18px 20px" }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontFamily: "Manrope, sans-serif", fontSize: "0.76rem", letterSpacing: "0.08em", textTransform: "uppercase", color: "#6a706d" }}>
            H5 Voice Session
          </div>
          <div style={{ marginTop: 12, width: 112, height: 112, borderRadius: "50%", background: "radial-gradient(circle at 30% 30%, #8fe5ff, #2d7eb8 56%, #17222f 100%)", marginInline: "auto" }} />
          <div style={{ fontFamily: "Georgia, serif", fontSize: "1.45rem", color: "#17222f", marginTop: 14 }}>
            Mei, AI Front Desk
          </div>
          <div style={{ fontFamily: "Manrope, sans-serif", fontSize: "0.8rem", color: "#60796c", marginTop: 4 }}>
            {property.propertyName} · {property.city}
          </div>
        </div>
        <div style={{ ...panel, marginTop: 18, padding: "1rem", borderRadius: 18, background: "rgba(255,255,255,0.78)" }}>
          <div style={{ fontFamily: "Manrope, sans-serif", fontSize: "0.72rem", fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: "#4f655e", marginBottom: 8 }}>
            Transcript
          </div>
          <div style={{ display: "grid", gap: 10 }}>
            <div style={{ background: "#f2f5f3", borderRadius: 14, padding: "10px 12px", fontFamily: "Manrope, sans-serif", fontSize: "0.84rem", color: "#2b3530", lineHeight: 1.55 }}>
              <strong>Guest:</strong> {mode.voiceGuest}
            </div>
            <div style={{ background: "#dff6e6", borderRadius: 14, padding: "10px 12px", fontFamily: "Manrope, sans-serif", fontSize: "0.84rem", color: "#214534", lineHeight: 1.55 }}>
              <strong>AI:</strong> {mode.voiceAi}
            </div>
          </div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 8, marginTop: 14 }}>
          {["Parking", "Wi-Fi", "Call human"].map((label) => (
            <div key={label} style={{ borderRadius: 14, padding: "10px 8px", textAlign: "center", background: "rgba(255,255,255,0.78)", fontFamily: "Manrope, sans-serif", fontSize: "0.78rem", fontWeight: 700, color: "#2e3834" }}>
              {label}
            </div>
          ))}
        </div>
        <div style={{ position: "absolute", left: 18, right: 18, bottom: 20 }}>
          <div style={{ textAlign: "center", fontFamily: "Manrope, sans-serif", fontSize: "0.8rem", color: "#61716a", marginBottom: 8 }}>
            Listening • reservation loaded • fallback text available
          </div>
          <div style={{ width: 84, height: 84, borderRadius: "50%", margin: "0 auto", background: "linear-gradient(135deg, #73d7ff, #d98c5f)", display: "flex", alignItems: "center", justifyContent: "center", color: "#071118", fontFamily: "Manrope, sans-serif", fontSize: "1.7rem", fontWeight: 900 }}>
            ●
          </div>
        </div>
      </div>
    </PhoneShell>
  );
}

function FollowupScreen({ modeKey, property }) {
  const mode = journeyModes[modeKey];
  const mappedLines = mode.followup.map((line) => (
    line
      .replace("HiddenPine_3A", property.wifi)
      .replace("11:00 AM", property.checkoutTime)
  ));
  return (
    <PhoneShell>
      <div style={{ padding: "18px 16px 20px" }}>
        <div style={{ display: "grid", gap: 12, marginTop: 20 }}>
          {mappedLines.map((line) => (
            <div key={line} style={{ background: line.includes("Human") ? "#daf4e2" : "#ffffff", borderRadius: 18, padding: "12px 14px", boxShadow: "0 10px 24px rgba(0,0,0,0.08)" }}>
              <div style={{ fontFamily: "Manrope, sans-serif", fontSize: "0.84rem", color: "#33423a", lineHeight: 1.6 }}>
                {line}
              </div>
            </div>
          ))}
        </div>
      </div>
    </PhoneShell>
  );
}

function OpsScreen({ modeKey, property }) {
  const mode = journeyModes[modeKey];
  return (
    <div style={{ ...panel, padding: "1.4rem" }}>
      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1.2fr) minmax(240px, 0.8fr)", gap: 16 }} className="md:grid-cols-1">
        <div style={{ display: "grid", gap: 14 }}>
          <div style={{ background: "rgba(255,255,255,0.04)", borderRadius: 18, padding: "14px 16px" }}>
            <div style={{ fontFamily: "Georgia, serif", fontSize: "1.2rem", color: "#f4f0eb", marginBottom: 8 }}>
              {property.propertyName} · {property.unit}
            </div>
            <Text size="s" as="p" style={{ color: "rgba(255,255,255,0.7)", lineHeight: 1.7 }}>
              {mode.opsSummary}
            </Text>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 10 }} className="sm:grid-cols-1">
            {[
              { label: "Confusion", value: "High at start" },
              { label: "Resolution", value: "No human takeover" },
              { label: "Risk", value: "Low after follow-up" },
            ].map((item) => (
              <div key={item.label} style={{ background: "rgba(255,255,255,0.04)", borderRadius: 16, padding: "12px 12px 10px" }}>
                <div style={{ fontFamily: "Manrope, sans-serif", fontSize: "0.72rem", color: "rgba(255,255,255,0.42)", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                  {item.label}
                </div>
                <div style={{ fontFamily: "Manrope, sans-serif", fontSize: "0.95rem", fontWeight: 800, color: "#fff", marginTop: 6 }}>
                  {item.value}
                </div>
              </div>
            ))}
          </div>
        </div>
        <div style={{ display: "grid", gap: 12 }}>
          <div style={{ background: "rgba(255,255,255,0.04)", borderRadius: 18, padding: "14px 14px 12px" }}>
            <div style={{ fontFamily: "Manrope, sans-serif", fontSize: "0.72rem", fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: "rgba(115,215,255,0.82)", marginBottom: 10 }}>
              Operator actions
            </div>
            <div style={{ display: "grid", gap: 8 }}>
              {mode.opsActions.map((item) => (
                <div key={item} style={{ borderRadius: 14, padding: "10px 12px", background: "rgba(255,255,255,0.05)", fontFamily: "Manrope, sans-serif", fontSize: "0.84rem", color: "rgba(255,255,255,0.74)" }}>
                  {item}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function WechatFrontDeskPrototypePage() {
  const navigate = useNavigate();
  const [index, setIndex] = useState(0);
  const [modeKey, setModeKey] = useState("access");
  const [property, setProperty] = useState({
    propertyName: "Hidden Pine Stay",
    city: "Hangzhou",
    guestName: "Lin",
    unit: "Unit 3A",
    checkInWindow: "9:00 PM",
    checkoutTime: "11:00 AM",
    wifi: "HiddenPine_3A",
    parking: "Bay B2 behind the bamboo wall",
    quietHours: "10 PM to 8 AM",
    escalationContact: "On-call host via WeCom in under 10 minutes",
  });
  const [launching, setLaunching] = useState(false);
  const [launchError, setLaunchError] = useState("");
  const active = steps[index];
  const screen = useMemo(() => {
    if (active.id === "entry") return <EntryScreen modeKey={modeKey} onSelectMode={setModeKey} property={property} />;
    if (active.id === "voice") return <VoiceScreen modeKey={modeKey} property={property} />;
    if (active.id === "followup") return <FollowupScreen modeKey={modeKey} property={property} />;
    return <OpsScreen modeKey={modeKey} property={property} />;
  }, [active.id, modeKey, property]);

  const advance = () => setIndex((current) => (current + 1) % steps.length);

  const launchLiveMvp = async () => {
    setLaunching(true);
    setLaunchError("");
    try {
      await ensureDemoSession();
      navigate("/demo/voice-agent", {
        state: {
          personaPrompt: buildFrontDeskPersonaPrompt(property, modeKey),
          businessName: `${property.propertyName} Front Desk`,
        },
      });
    } catch (error) {
      setLaunchError(error.message || "Could not start the live demo.");
    }
    setLaunching(false);
  };

  const updateProperty = (key, value) => {
    setProperty((current) => ({ ...current, [key]: value }));
  };

  return (
    <>
      <Helmet>
        <title>WeChat Front Desk Prototype — AIHumanity</title>
        <meta
          name="description"
          content="Interactive prototype for a WeChat-first AI front desk: WeChat entry, H5 voice session, persistent follow-up, and operator action view."
        />
      </Helmet>

      <div className="w-full bg-black-900_02">
        <TopMenuBar />

        <section style={{ paddingTop: 124, paddingBottom: 72, position: "relative", overflow: "hidden" }}>
          <div style={{ position: "absolute", inset: 0, background: "radial-gradient(circle at 14% 18%, rgba(115,215,255,0.16), transparent 34%), radial-gradient(circle at 82% 18%, rgba(217,140,95,0.14), transparent 28%), linear-gradient(180deg, rgba(5,6,8,0.84), rgba(5,6,8,1))", pointerEvents: "none" }} />
          <div style={{ ...pageShell, position: "relative", zIndex: 1 }}>
            <Badge>WeChat / H5 Prototype</Badge>
            <Heading size="3xl" as="h1" className="!text-gray-300 mt-5" style={{ maxWidth: 760, lineHeight: "1.03" }}>
              Dedicated prototype for the rental-stay front desk flow.
            </Heading>
            <Text size="md" as="p" style={{ maxWidth: 780, marginTop: 18, color: "rgba(255,255,255,0.66)", lineHeight: 1.8 }}>
              This page isolates the actual product flow: WeChat entry, H5 voice support, persistent follow-up, and operator-side service-quality control.
            </Text>
          </div>
        </section>

        <section style={{ paddingBottom: 96 }}>
          <div style={pageShell}>
            <div style={{ ...panel, padding: "1.5rem", marginBottom: 18, display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(280px, 0.9fr)", gap: 20, alignItems: "start" }} className="md:grid-cols-1">
              <div>
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                  {steps.map((step, stepIndex) => (
                    <StepPill key={step.id} active={stepIndex === index} onClick={() => setIndex(stepIndex)}>
                      {step.label}
                    </StepPill>
                  ))}
                </div>
                <div style={{ marginTop: 18 }}>
                  <div style={{ fontFamily: "Georgia, serif", fontSize: "1.42rem", color: "#f4f0eb" }}>{active.title}</div>
                  <Text size="s" as="p" style={{ marginTop: 10, color: "rgba(255,255,255,0.68)", lineHeight: 1.75, maxWidth: 620 }}>
                    {active.description}
                  </Text>
                </div>
              </div>
              <div style={{ background: "rgba(255,255,255,0.03)", borderRadius: 18, padding: "14px 16px" }}>
                <div style={{ fontFamily: "Manrope, sans-serif", fontSize: "0.72rem", fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: "rgba(217,140,95,0.9)", marginBottom: 8 }}>
                  Prototype intent
                </div>
                <Text size="s" as="p" style={{ color: "rgba(255,255,255,0.74)", lineHeight: 1.75 }}>
                  Validate interaction trust, voice usability, and operator visibility before building deep WeChat-native or PMS-integrated production flows.
                </Text>
                <div style={{ marginTop: 12, borderRadius: 14, padding: "12px 12px 10px", background: "rgba(255,255,255,0.04)" }}>
                  <div style={{ fontFamily: "Manrope, sans-serif", fontSize: "0.72rem", fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: "rgba(115,215,255,0.82)", marginBottom: 6 }}>
                    Active guest path
                  </div>
                  <Text size="s" as="p" style={{ color: "rgba(255,255,255,0.76)", lineHeight: 1.75 }}>
                  {journeyModes[modeKey].label}
                </Text>
                </div>
                <button
                  type="button"
                  onClick={launchLiveMvp}
                  disabled={launching}
                  style={{
                    marginTop: 14,
                    width: "100%",
                    padding: "0.88rem 1rem",
                    borderRadius: 14,
                    border: "none",
                    background: "linear-gradient(135deg, #73d7ff, #5ed5b7 48%, #d98c5f)",
                    color: "#fff",
                    fontFamily: "Manrope, sans-serif",
                    fontSize: "0.9rem",
                    fontWeight: 800,
                    cursor: launching ? "progress" : "pointer",
                    opacity: launching ? 0.8 : 1,
                  }}
                >
                  {launching ? "Starting live voice MVP..." : "Launch live voice MVP"}
                </button>
                {launchError && (
                  <Text size="s" as="p" style={{ color: "#ff9e9e", lineHeight: 1.6, marginTop: 10 }}>
                    {launchError}
                  </Text>
                )}
                <button
                  type="button"
                  onClick={advance}
                  style={{
                    marginTop: 14,
                    width: "100%",
                    padding: "0.88rem 1rem",
                    borderRadius: 14,
                    border: "none",
                    background: "linear-gradient(135deg, #73d7ff, #5ed5b7 48%, #d98c5f)",
                    color: "#fff",
                    fontFamily: "Manrope, sans-serif",
                    fontSize: "0.9rem",
                    fontWeight: 800,
                    cursor: "pointer",
                  }}
                >
                  Next step
                </button>
              </div>
            </div>

            {screen}

            <div style={{ ...panel, marginTop: 18, padding: "1.35rem" }}>
              <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)", gap: 18 }} className="md:grid-cols-1">
                <div>
                  <div style={{ fontFamily: "Manrope, sans-serif", fontSize: "0.76rem", fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: "rgba(115,215,255,0.82)", marginBottom: 12 }}>
                    Web MVP Setup
                  </div>
                  <div style={{ display: "grid", gap: 12 }}>
                    <label style={{ display: "grid", gap: 6 }}>
                      <span style={{ fontFamily: "Manrope, sans-serif", fontSize: "0.8rem", color: "rgba(255,255,255,0.62)" }}>Property name</span>
                      <input style={fieldStyle} value={property.propertyName} onChange={(event) => updateProperty("propertyName", event.target.value)} />
                    </label>
                    <label style={{ display: "grid", gap: 6 }}>
                      <span style={{ fontFamily: "Manrope, sans-serif", fontSize: "0.8rem", color: "rgba(255,255,255,0.62)" }}>City</span>
                      <input style={fieldStyle} value={property.city} onChange={(event) => updateProperty("city", event.target.value)} />
                    </label>
                    <label style={{ display: "grid", gap: 6 }}>
                      <span style={{ fontFamily: "Manrope, sans-serif", fontSize: "0.8rem", color: "rgba(255,255,255,0.62)" }}>Guest name</span>
                      <input style={fieldStyle} value={property.guestName} onChange={(event) => updateProperty("guestName", event.target.value)} />
                    </label>
                    <label style={{ display: "grid", gap: 6 }}>
                      <span style={{ fontFamily: "Manrope, sans-serif", fontSize: "0.8rem", color: "rgba(255,255,255,0.62)" }}>Unit</span>
                      <input style={fieldStyle} value={property.unit} onChange={(event) => updateProperty("unit", event.target.value)} />
                    </label>
                  </div>
                </div>

                <div>
                  <div style={{ fontFamily: "Manrope, sans-serif", fontSize: "0.76rem", fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: "rgba(217,140,95,0.9)", marginBottom: 12 }}>
                    Guest support config
                  </div>
                  <div style={{ display: "grid", gap: 12 }}>
                    <label style={{ display: "grid", gap: 6 }}>
                      <span style={{ fontFamily: "Manrope, sans-serif", fontSize: "0.8rem", color: "rgba(255,255,255,0.62)" }}>Check-in window</span>
                      <input style={fieldStyle} value={property.checkInWindow} onChange={(event) => updateProperty("checkInWindow", event.target.value)} />
                    </label>
                    <label style={{ display: "grid", gap: 6 }}>
                      <span style={{ fontFamily: "Manrope, sans-serif", fontSize: "0.8rem", color: "rgba(255,255,255,0.62)" }}>Checkout time</span>
                      <input style={fieldStyle} value={property.checkoutTime} onChange={(event) => updateProperty("checkoutTime", event.target.value)} />
                    </label>
                    <label style={{ display: "grid", gap: 6 }}>
                      <span style={{ fontFamily: "Manrope, sans-serif", fontSize: "0.8rem", color: "rgba(255,255,255,0.62)" }}>Wi-Fi</span>
                      <input style={fieldStyle} value={property.wifi} onChange={(event) => updateProperty("wifi", event.target.value)} />
                    </label>
                    <label style={{ display: "grid", gap: 6 }}>
                      <span style={{ fontFamily: "Manrope, sans-serif", fontSize: "0.8rem", color: "rgba(255,255,255,0.62)" }}>Parking guidance</span>
                      <input style={fieldStyle} value={property.parking} onChange={(event) => updateProperty("parking", event.target.value)} />
                    </label>
                    <label style={{ display: "grid", gap: 6 }}>
                      <span style={{ fontFamily: "Manrope, sans-serif", fontSize: "0.8rem", color: "rgba(255,255,255,0.62)" }}>Quiet hours</span>
                      <input style={fieldStyle} value={property.quietHours} onChange={(event) => updateProperty("quietHours", event.target.value)} />
                    </label>
                    <label style={{ display: "grid", gap: 6 }}>
                      <span style={{ fontFamily: "Manrope, sans-serif", fontSize: "0.8rem", color: "rgba(255,255,255,0.62)" }}>Escalation contact</span>
                      <input style={fieldStyle} value={property.escalationContact} onChange={(event) => updateProperty("escalationContact", event.target.value)} />
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <div style={{ ...panel, marginTop: 18, padding: "1.35rem", display: "flex", gap: 14, flexWrap: "wrap", justifyContent: "space-between", alignItems: "center" }}>
              <Text size="s" as="p" style={{ color: "rgba(255,255,255,0.68)", lineHeight: 1.75, maxWidth: 680 }}>
                Keep `/demo/voice-host` as the narrative bridge. Use this page as the web-first MVP surface while WeChat deployment and testing remain a separate integration step.
              </Text>
              <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                <Link to="/demo/voice-host" style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", padding: "0.82rem 1.2rem", borderRadius: 14, textDecoration: "none", fontFamily: "Manrope, sans-serif", fontSize: "0.92rem", fontWeight: 700, color: "#fff", border: "1px solid rgba(255,255,255,0.14)" }}>
                  Back to scenario page
                </Link>
                <Link to="/demo/voice-agent" style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", padding: "0.82rem 1.2rem", borderRadius: 14, textDecoration: "none", fontFamily: "Manrope, sans-serif", fontSize: "0.92rem", fontWeight: 700, color: "#fff", background: "linear-gradient(135deg, #73d7ff, #5ed5b7 48%, #d98c5f)" }}>
                  Open live voice layer
                </Link>
              </div>
            </div>
          </div>
        </section>

        <FooterSection />
      </div>
    </>
  );
}
